import { faMagnifyingGlass, faXmark } from '@fortawesome/pro-light-svg-icons';
import { faFilter } from '@fortawesome/pro-regular-svg-icons';
import { faArrowsRotate, faColumns3 } from '@fortawesome/pro-solid-svg-icons';
import Avatar from 'Components/Atoms/Avatar/Avatar';
import Button from 'Components/Atoms/Button/Button';
import Dropdown, {DropdownConsumer} from 'Components/Atoms/Dropdown/Dropdown';
import TextField from 'Components/Atoms/Fields/TextField/TextField';
import FontAwesomeIcon from 'Components/Atoms/FontAwesomeIcon/FontAwesomeIcon';
import Loader from 'Components/Atoms/Loader/Loader';
import ButtonGroup from 'Components/Molecules/ButtonGroup/ButtonGroup';
import AvatarGroup from 'Components/Molecules/Group/AvatarGroup/AvatarGroup';
import ColumnModal from 'Components/Organisms/ColumnModal/ColumnModal';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useUserMetadata } from 'Services/Authentication/Auth0/Auth0Service';
import { InternalDatasets } from 'Services/helpers';
import EntityHelper from 'Services/Settings/Entity/EntityHelper';
import { SUBSTATUS } from 'Services/Status/Substatus.types';
import { fetchBusinessUnitDataset } from 'Store/BusinessUnit/Actions';
import { getBusinessUnitDataset } from 'Store/BusinessUnit/Selectors';
import { fetchCandidateDataset } from 'Store/Candidate/Actions';
import { getCandidateDataset } from 'Store/Candidate/Selectors';
import { fetchContactDataset } from 'Store/Contact/Actions';
import { getContactDataset } from 'Store/Contact/Selectors';
import { fetchAllDatasets } from 'Store/FieldManagement/Actions';
import { FieldType } from 'Store/FieldManagement/PropTypes';
import { getDatasetLabels } from 'Store/FieldManagement/Selectors';
import { StoreState } from 'Store/Reducers';
import { getEntityById } from 'Store/Settings/Entities/Selectors';
import { fetchEntityTypesByEntityId } from 'Store/Settings/EntityTypes/Actions';
import { getEntityTypesByEntityId, getEntityTypesByIdAsObject } from 'Store/Settings/EntityTypes/Selector';
import { getFieldsById } from 'Store/Settings/Fields/Selectors';
import { selectEntityPaginationByEntityName, selectEntityStatisticsByEntityName } from 'Store/Shared/Store/Values/Selector';
import { fetchUsersDataset } from 'Store/User/Actions';
import { getUserDataset } from 'Store/User/Selectors';
import { fetchVacancyDataset } from 'Store/Vacancy/Actions';
import { getVacancyDataset } from 'Store/Vacancy/Selectors';
import styles from './EntityOverviewHeader.module.sass';

const EntityOverviewFilterModal = React.lazy(() => import('../EntityOverviewFilterModal/EntityOverviewFilterModal'));

const EntityOverviewHeader = ({
    entity,
    onFilterChange, filters,
    fieldsById, datasetLabelsById,
    userDatasetById, businessUnitDatasetById, contactDatasetById, vacancyDatasetById, candidateDatasetById,
    
    onRefresh, entityTypesById,
    pagination, statistics,
    entityTypes,
    ...props
}: EntityOverviewHeaderProps) => {
    const user_metadata = useUserMetadata();
    const [state, setState] = useState({
        isColumnModalOpen: false,
        isFilterModalOpen: false,
        searchQuery: filters?.query ?? ''
    });
    const [searchTimer, setSearchTimer] = useState<any>(null);

    const handleColumnModal = () => setState((state) => ({
        ...state,
        isColumnModalOpen: !state.isColumnModalOpen
    }));

    const handleFilterModal = () => setState((state) => ({
        ...state,
        isFilterModalOpen: !state.isFilterModalOpen
    }));

    useEffect(() => {
        props.fetchEntityTypesByEntityId(entity?.id);
        user_metadata?.overview?.[entity.name]?.forEach(i => {
            const dataset = fieldsById?.[i]?.default?.dataset?.[0];
            if (!dataset) return;

            if (dataset === InternalDatasets.BUSINESS_UNITS) {
                props.fetchBusinessUnitDataset();
            } else if (dataset === InternalDatasets.CANDIDATES) {
                props.fetchCandidateDataset();
            } else if (dataset === InternalDatasets.VACANCIES) {
                props.fetchVacancyDataset();
            } else if (dataset === InternalDatasets.CONTACT) {
                props.fetchContactDataset();
            } else if (dataset === InternalDatasets.USERS) { 
                props.fetchUsersDataset();
            } else {
                props.fetchAllDatasets();
            }
        });
    }, []);

    const handleRemoveFilter = (i: any) => () => {
        const a = {...filters};
        delete a[i];

        handleFilterChange(a);
    }

    const handleFilterChange = (filter) => onFilterChange({...filter});

    const handleEntityTypeFilter = (i: any) => () => {
        let newFilters = {...filters};
        
        for (const valueItem of [i]) {
            if (newFilters?.['type']?.includes(valueItem)) {
                newFilters['type'] = newFilters['type'].filter(i => i !== valueItem);
                if (newFilters['type'].length === 0) {
                    delete newFilters['type'];
                }
            } else {
                newFilters['type'] = newFilters?.['type'] ?? [];
                newFilters['type'].push(valueItem);
            }
        }

        handleFilterChange(newFilters);
    }

    const handleSearchQuery = (value: string) => {
        if (value.trim() == state.searchQuery.trim()) return;

        clearTimeout(searchTimer);
        setSearchTimer(null);

        setState((state) => ({
            ...state,
            searchQuery: value
        }));
        
        setSearchTimer(
            setTimeout(() => {
                if (value) {
                    filters = {
                        ...filters,
                        query: value
                    };
                } else {
                    delete filters['query'];

                    filters = { ...filters };
                }

                handleFilterChange(filters);
            }, 500)
        );
    };

    const activeFilterKeys = useMemo(() =>
        Object.keys(filters)
        ?.filter(i => !['query', 'type'].includes(i))
    , [filters]);
    
    return <>
        {
            state.isColumnModalOpen &&
            <ColumnModal
                entity_id={entity.id}
                onClose={handleColumnModal}
            />
        }

        <div className={styles['header']}>
            <div className={styles['header__bar']}>
                <div className={styles['header__bar-left']}>
                    <div className={styles['header__bar-left-search']}>
                        <TextField
                            iconBefore={<FontAwesomeIcon icon={faMagnifyingGlass} />}
                            size='large'
                            placeholder={`Zoeken op naam...`}
                            disableBlur

                            value={state.searchQuery}
                            onChange={handleSearchQuery}
                        />
                    </div>

                    <ButtonGroup gap='none'>
                        {
                            entityTypes && entityTypes.map(i =>
                                <Button
                                    variant='solid'
                                    color='light'
                                    size='large'
                                    onClick={handleEntityTypeFilter(i?.id)}
                                    isActive={filters?.['type']?.includes(i?.id)}
                                >
                                    Type: { i?.name }
                                </Button>
                            )
                        }
                    </ButtonGroup>

                    <Dropdown
                        disableCloseOnOutsideClick
                        disableCloseOnClick
                        triggerAction='click'
                        trigger={
                            <Button
                                variant='transparent'
                                color='primary'
                                size='large'
                                iconBefore={<FontAwesomeIcon icon={faFilter} />}
                            >
                                Filters beheren { activeFilterKeys.length > 0 && `(${activeFilterKeys.length})` }
                            </Button>
                        }
                    >
                        <Suspense
                            fallback={
                                <Loader />
                            }
                        >
                            <EntityOverviewFilterModal
                                entity_id={entity.id}
                                filters={filters}
                                onFilterChange={handleFilterChange}
                            />
                        </Suspense>
                    </Dropdown>
                </div>

                <ButtonGroup>
                    <Button
                        variant='solid'
                        color='light'
                        size='large'
                        iconBefore={<FontAwesomeIcon icon={faArrowsRotate} />}
                        onClick={onRefresh}
                    >
                        Herladen
                    </Button>
                    <Button
                        variant='solid'
                        color='light'
                        size='large'
                        iconBefore={<FontAwesomeIcon icon={faColumns3} />}
                        onClick={handleColumnModal}
                    >
                        Kolommen instellen
                    </Button>
                </ButtonGroup>
            </div>

            {
                Object.keys(filters).length > 0 &&
                <>
                    <div className={styles['header__filters']}>
                        {
                            activeFilterKeys
                                ?.map(i => {
                                    const field = fieldsById?.[i];
                                    let name = field?.name ?? '';
                                    let mediator = 'is gelijk aan';
                                    let value: any = '';

                                    switch (i) {
                                        case 'status':
                                            name = 'Status';
                                            value = filters[i]
                                                ?.map((i: any, idx: number, array: any[]) =>
                                                    <>{
                                                        i === null
                                                        ? 'Geen waarde'
                                                        : SUBSTATUS.getLabelByValue(i)
                                                    }{idx !== array.length-1 && ', '}</>
                                                )
                                            break;
                                        case 'owners':
                                            name = 'Intern verantwoordelijke';
                                            value = <AvatarGroup>
                                                {
                                                    filters[i]?.map(i =>
                                                        <Avatar src={userDatasetById?.[i]?.picture}>
                                                            { userDatasetById?.[i]?.name }
                                                        </Avatar>
                                                    )
                                                }
                                            </AvatarGroup>;
                                            break;
                                        case 'updated_between':
                                            name = 'Aangepast';
                                            mediator = 'tussen';
                                            value = filters[i]?.[0].replaceAll(',', ' en ');
                                            break;
                                        case 'created_between':
                                            name = 'Aangemaakt';
                                            mediator = 'tussen';
                                            value = filters[i]?.[0].replaceAll(',', ' en ');
                                            break;
                                        default:
                                            if (!field?.type) {
                                                const fieldIds = i?.split('+');
                                                const fieldId = fieldIds?.[0];
                                                const combinationFieldId = fieldIds?.[1];

                                                name = fieldsById?.[combinationFieldId]?.name + ' - ' + fieldsById?.[fieldId]?.name;
                                            }

                                            if (field?.type === FieldType.Toggle) {
                                                value = filters[i]?.[0] === 1 ? 'Ja' : filters[i]?.[0] === 0 ? 'Nee' : 'Geen waarde';
                                            } else if (field?.type === FieldType.Address) {
                                                mediator = 'rondom';
                                                let { originalState, radius } = JSON.parse(filters[i]);
                                                value = originalState?.address?.address?.locality + ` (${radius} km)`;
                                            } else  {
                                                value = filters[i]
                                                ?.map((j: any, idx: number, array: any[]) =>
                                                    <>{
                                                        j === null
                                                        ? 'Geen waarde'
                                                        : datasetLabelsById?.[j]
                                                        ?? businessUnitDatasetById?.[j]
                                                        ?? contactDatasetById?.[j]
                                                        ?? vacancyDatasetById?.[j]
                                                        ?? candidateDatasetById?.[j]
                                                        ?? j
                                                    }{idx !== array.length-1 && ', '}</>
                                                );
                                            }
                                    }

                                    return <div className={styles['header__filters-item']}>
                                        <div className={styles['header__filters-item-title']}>
                                            { name }
                                        </div>
                                        <div className={styles['header__filters-item-operator']}>
                                            { mediator }
                                        </div>
                                        <div className={styles['header__filters-item-values']}>
                                            { value }
                                        </div>
                                        <div className={styles['header__filters-item-button']}>
                                            <Button
                                                variant='transparent'
                                                size='small'
                                                color='dark'
                                                iconBefore={<FontAwesomeIcon icon={faXmark} size={'lg'} />}
                                                onClick={handleRemoveFilter(i)}
                                            />
                                        </div>
                                    </div>;
                                }
                            )
                        }
                    </div>
                    <div className={styles['header__total']}>
                        <div className={styles['header__total-strong']}>{ pagination?.records ?? 0 }</div> van de <div className={'header__total-strong'}>{ statistics?.total?.records } {EntityHelper.getDisplayValueByName(entity.name)?.toLowerCase()}</div> gevonden
                    </div>
                </>
            }
        </div>
    </>;
}

interface Props {
    entity_id: string
    onFilterChange: any
    filters: any
    onRefresh: any
}

const mapState = (state: StoreState, props: Props) => ({
    entity: getEntityById(state, props.entity_id),
    entityTypes: getEntityTypesByEntityId(state, props?.entity_id),
    fieldsById: getFieldsById(state),
    datasetLabelsById: getDatasetLabels(state),

    entityTypesById: getEntityTypesByIdAsObject(state),

    pagination: selectEntityPaginationByEntityName(state, getEntityById(state, props.entity_id)?.name),
    statistics: selectEntityStatisticsByEntityName(state, getEntityById(state, props.entity_id)?.name),

    userDatasetById: getUserDataset(state),
    businessUnitDatasetById: getBusinessUnitDataset(state),
    contactDatasetById: getContactDataset(state),
    vacancyDatasetById: getVacancyDataset(state),
    candidateDatasetById: getCandidateDataset(state)
});

const mapDispatch = {
    fetchAllDatasets,
    fetchEntityTypesByEntityId,
    
    fetchBusinessUnitDataset,
    fetchVacancyDataset,
    fetchCandidateDataset,
    fetchContactDataset,
    fetchUsersDataset
};

type EntityOverviewHeaderProps = Props & ReturnType<typeof mapState> & typeof mapDispatch;

export default connect(mapState, mapDispatch)(EntityOverviewHeader);